import React, { useEffect, useMemo, useState } from 'react';
import { faqSections } from './data';
import './index.scss';

const getItemKey = (sectionId, itemIndex) => `${sectionId}-${itemIndex}`;

const matchesQuery = (item, query) => {
  if (!query) return true;

  return [item.title, item.cause, item.solution, item.judgement]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('zh-CN')
    .includes(query);
};

const FaqAnswer = ({ item }) => (
  <div className="faq-page__answer-inner">
    <p>
      <span className="faq-page__tag faq-page__tag--cause">原因</span>
      <span>{item.cause}</span>
    </p>
    <p>
      <span className="faq-page__tag faq-page__tag--solution">方案</span>
      <span>{item.solution}</span>
    </p>
    {item.judgement && (
      <p>
        <span className="faq-page__tag faq-page__tag--judgement">判断</span>
        <span>{item.judgement}</span>
      </p>
    )}
    {item.image && (
      <img className="faq-page__image" src={item.image} alt={item.imageAlt} loading="lazy" />
    )}
  </div>
);

const FaqPage = () => {
  const [query, setQuery] = useState('');
  const [openItems, setOpenItems] = useState(new Set());
  const [activeSection, setActiveSection] = useState(faqSections[0].id);
  const normalizedQuery = query.trim().toLocaleLowerCase('zh-CN');

  const visibleSections = useMemo(
    () =>
      faqSections
        .map((section) => ({
          ...section,
          items: section.items
            .map((item, originalIndex) => ({ ...item, originalIndex }))
            .filter((item) => matchesQuery(item, normalizedQuery))
        }))
        .filter((section) => section.items.length > 0),
    [normalizedQuery]
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '智能计量设备常见问题｜快速排查指南';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const sections = faqSections
      .map((section) => document.getElementById(section.id))
      .filter(Boolean);
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (left, right) =>
              sections.indexOf(left.target) - sections.indexOf(right.target)
          );
        if (visible.length > 0) setActiveSection(visible[visible.length - 1].target.id);
      },
      { rootMargin: '-18% 0px -68%', threshold: 0 }
    );
    const footer = document.querySelector('.faq-page__footer');
    const footerObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setActiveSection(faqSections[faqSections.length - 1].id);
        }
      },
      { threshold: 0.1 }
    );

    sections.forEach((section) => sectionObserver.observe(section));
    if (footer) footerObserver.observe(footer);
    return () => {
      sectionObserver.disconnect();
      footerObserver.disconnect();
    };
  }, [normalizedQuery]);

  const toggleItem = (key) => {
    setOpenItems((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="faq-page">
      <header className="faq-page__hero" id="top">
        <div className="faq-page__hero-inner">
          <p className="faq-page__eyebrow">SMART METERING SUPPORT</p>
          <h1>智能计量设备<br />常见问题</h1>
          <p className="faq-page__hero-copy">面向安装、运维与售后现场的快速排查指南。先确认供电与接线，再检查通信链路和平台参数。</p>
        </div>
      </header>

      <nav className="faq-page__nav-shell" aria-label="问题分类">
        <div className="faq-page__nav">
          <a className="faq-page__top-link" href="#top" aria-label="返回顶部">↑</a>
          {faqSections.map((section) => (
            <a
              key={section.id}
              className={activeSection === section.id ? 'is-active' : ''}
              href={`#${section.id}`}
              onClick={() => setActiveSection(section.id)}
            >
              {section.number} {section.shortTitle}
            </a>
          ))}
        </div>
      </nav>

      <main className="faq-page__main">
        <div className="faq-page__tools" role="search">
          <label className="faq-page__search">
            <span className="faq-page__sr-only">搜索问题</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索故障、原因或解决方案…"
              autoComplete="off"
            />
          </label>
          <button
            className="faq-page__clear"
            type="button"
            disabled={!query}
            onClick={() => setQuery('')}
          >
            清除
          </button>
        </div>

        {visibleSections.map((section) => (
          <section className="faq-page__section" id={section.id} key={section.id}>
            <div className="faq-page__section-head">
              <div className="faq-page__section-title">
                <span>{section.number}</span>
                <h2>{section.title}</h2>
              </div>
              <span className="faq-page__count">{section.items.length} 项</span>
            </div>

            <div className="faq-page__list">
              {section.items.map((item) => {
                const key = getItemKey(section.id, item.originalIndex);
                const isOpen = Boolean(normalizedQuery) || openItems.has(key);
                const answerId = `faq-answer-${key}`;

                return (
                  <article className={`faq-page__item${isOpen ? ' is-open' : ''}`} key={key}>
                    <button
                      className="faq-page__question"
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={answerId}
                      onClick={() => toggleItem(key)}
                    >
                      <span className="faq-page__number">
                        {String(item.originalIndex + 1).padStart(2, '0')}
                      </span>
                      <span className="faq-page__question-title">{item.title}</span>
                      <span className="faq-page__toggle" aria-hidden="true" />
                    </button>
                    <div className="faq-page__answer" id={answerId} hidden={!isOpen}>
                      <FaqAnswer item={item} />
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        {visibleSections.length === 0 && (
          <div className="faq-page__empty" role="status">
            <b>未找到相关问题</b>
            <span>换一个关键词试试，例如“离线”“电压”或“接线”。</span>
          </div>
        )}
      </main>

      <footer className="faq-page__footer">
        <b>智能计量 · 快速排查指南</b>
        <span>涉及带电作业时，请由具备资质的专业人员操作。</span>
      </footer>
    </div>
  );
};

export default FaqPage;
